/**
 * Details einer Stellplatz-Belegung (Klick oder Tipp auf den Balken): Kennzeichen, Stand aus
 * `paid`/`status`, Gast oder Notiz, Apartment, Buchung, Zeitraum mit „Frei ab“ und „Buchung öffnen“.
 */
import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import type { ParkingOccupancyEntry } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { PARKING_TONE, parkingState } from './parkingCalendarHelpers';

const day = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
const nights = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);

export function ParkingReservationDetails({ entry }: { entry: ParkingOccupancyEntry }) {
  const block = entry.kind === 'Block';
  const state = parkingState(entry);
  const facts: [string, string | null][] = [
    [block ? 'Notiz' : 'Gast', entry.guestName],
    ['Apartment', entry.propertyName],
    ['Buchung', entry.bookingReference],
    ['Von', day(entry.startDate)],
    // Der Abreisetag ist exklusiv – „bis“ allein läse sich, als stünde der Wagen noch da.
    ['Frei ab', day(entry.endDate)],
    ['Nächte', String(nights(entry.startDate, entry.endDate))],
  ];
  return (
    <div className="flex flex-col gap-3">
      <span className="flex items-center gap-2">
        <b className="grow font-mono text-base">
          {entry.licensePlate ?? (block ? 'Gesperrt' : 'Ohne Kennzeichen')}
        </b>
        <span
          className={cn(
            'rounded-pill px-2.5 py-0.5 text-[11px] font-bold',
            state === 'Gesperrt' ? 'bg-card-alt text-ink-soft' : PARKING_TONE[state],
          )}
        >
          {state}
        </span>
      </span>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[13px]">
        {facts.map(([label, value]) =>
          value ? (
            <div key={label} className="contents">
              <dt className="text-ink-soft">{label}</dt>
              <dd className="text-right font-semibold">{value}</dd>
            </div>
          ) : null,
        )}
      </dl>
      {!entry.licensePlate && !block && (
        <p className="text-amber-ink text-xs">
          Kein Kennzeichen hinterlegt – ein fremder Wagen lässt sich hier nicht zuordnen.
        </p>
      )}
      {entry.bookingId && (
        <Link
          to="/bookings/$id"
          params={{ id: entry.bookingId }}
          className="text-forest flex min-h-11 items-center gap-1 text-sm font-bold md:min-h-0"
        >
          Buchung öffnen
          <ArrowRight size={14} aria-hidden />
        </Link>
      )}
    </div>
  );
}
